// Stencil
import { Component, Host, h, Prop, Element, State, Watch } from "@stencil/core";

// Other Libraries
import {
  ChCheckboxCustomEvent,
  ChEditCustomEvent,
  ComboBoxModel,
  TabModel,
  TreeViewItemModel
} from "@genexus/chameleon-controls-library";
import { TreeViewItemModelExtended } from "@genexus/chameleon-controls-library";
import { MercuryBundles, getIconPath } from "@genexus/mercury";

// Custom Imports
import { config } from "../../common/config";
import { mapStylesToComboBoxItemModel } from "./helpers";
import { Locale } from "../../common/locale";
import { EntityData, GxOption } from "../../common/types";
import { TAB_DATA, DESIGN_TYPE_TO_TAB_MODEL_MAP, TAB_MODEL } from "./models";

const DESIGN_DATA_TYPE_PANELS = "panels";
const DESIGN_DATA_TYPE_STENCILS = "stencils";
const DESIGN_DATA_TYPE_IMAGES = "images";
const DESIGN_DATA_TYPE_DESIGN_SYSTEM = "design_system";
const DESIGN_DATA_TYPE_FONTS = "fonts";

const ITEM_COMPOSE_ID_SEPARATOR = "@@@";

// ch-checkbox
const CHECKED_VALUE = "on";

// Icons
const MENU_DELETE = getIconPath({
  category: "menus",
  name: "delete",
  colorType: "primary"
});
const DESIGN = getIconPath({
  category: "objects",
  name: "design"
});
const FILE_UPLOAD = getIconPath({
  category: "controls",
  name: "file-upload"
});
// For empty states
const WEBPANEL_ICON = getIconPath({
  category: "objects",
  name: "webpanel"
});
const PANEL_FOR_SD_ICON = getIconPath({
  category: "objects",
  name: "panel-for-sd"
});

const CSS_BUNDLES: MercuryBundles = [
  "resets/box-sizing",
  "utils/form",
  "utils/layout",
  "utils/typography",
  "utils/spacing",
  "components/button",
  "components/checkbox",
  "components/edit",
  "components/tree-view",
  "components/combo-box",
  "components/tab",
  "components/tooltip",
  "chameleon/scrollbar"
];

@Component({
  tag: "gx-ide-design-import",
  styleUrl: "design-import.scss",
  shadow: true,
  assetsDirs: ["gx-ide-assets/design-import"]
})
export class GxIdeImportFromDesign {
  /**
   * The component hard-coded strings translations.
   */
  // eslint-disable-next-line @stencil-community/own-props-must-be-private
  #componentLocale: any;

  /**
   * An array that keeps track of the tree items id's (design system data type
   * + item id) along with the content, that will be used to save and retrieve
   * items information, in order to avoid using a callback to get items
   * information that was already get. loadedItemsArray will be reset every time
   * a new design is loaded.
   */
  // eslint-disable-next-line @stencil-community/own-props-must-be-private
  #loadedItems: Map<string, ItemLoadedInfo> = new Map();

  #emptyStateAnimated: boolean = false;
  #formattedExtensions: string;
  #objectTreeCheckedStatus: ObjectTreeCheckedStatus = {
    all: [],
    none: ["-"]
  };

  // 2. REFERENCE TO ELEMENTS //

  @Element() el: HTMLGxIdeDesignImportElement;

  /* References needed to collect data */
  #accessTokenEl!: HTMLChEditElement;
  #importAsWebPanelsEl!: HTMLChCheckboxElement;
  #moduleEl!: HTMLGxIdeEntitySelectorElement;
  #messagesList: HTMLElement;
  #panelsSelected: string[] = [];
  #stencilsSelected: string[] = [];
  #imagesSelected: string[] = [];
  #fontsSelected: string[] = [];
  #fileInputHiddenEl: HTMLInputElement;
  #pathEl: HTMLChEditElement;

  // Use to force a re-render
  @State() reRenderCounter: number = 0;

  // Maps to Chameleon Models:
  @State() stylesAsComboBoxModel: ComboBoxModel;

  // The current style value
  @State() styleValue: string = "";

  @State() accessTokenHasError: boolean = false;

  @State() pathIsValid: boolean;
  @Watch("pathIsValid")
  pathIsValidChanged(pathIsValid: boolean) {
    if (pathIsValid) {
      this.#evaluateAccessTokenRender();
    } else {
      // hide access token input if path is not valid
      this.requiresAccessToken = false;
    }
  }

  @State() tabsModel: TabModel = [];

  @State() activeTabs: Map<DesignType, string> = new Map();

  @State() renderedTabs: Set<string> = new Set();

  /**
   * The design data loaded from file
   */
  @State() designData: DesignData = null;
  @Watch("designData")
  designDataChanged(newDesignData: DesignData) {
    if (newDesignData) {
      // panels
      const panelsSelectedItem = this.#getPanelsSelectedItem(
        this.designData.panels
      );
      const isForWeb = this.#importAsWebPanelsEl.value === CHECKED_VALUE;
      const panelsIconSrc = isForWeb
        ? "objects/webpanel"
        : "objects/panel-for-sd";

      // images
      const imagesIconSrc = "objects/image";

      this.designDataTreeDataModel = [
        {
          order: 0,
          id: `parent${ITEM_COMPOSE_ID_SEPARATOR}${DESIGN_DATA_TYPE_PANELS}`,
          leaf: false,
          caption: isForWeb
            ? this.#componentLocale.tree.webPanels
            : this.#componentLocale.tree.panels,
          startImgSrc: panelsIconSrc,
          checkbox: true,
          checked: true,
          expanded: true,
          items: this.#recursiveDesignTreeItems(
            this.designData.panels,
            DESIGN_DATA_TYPE_PANELS,
            panelsIconSrc,
            panelsSelectedItem
          )
        },
        {
          order: 1,
          id: `parent${ITEM_COMPOSE_ID_SEPARATOR}${DESIGN_DATA_TYPE_STENCILS}`,
          leaf: false,
          caption: this.#componentLocale.tree.stencils,
          startImgSrc: "objects/stencil",
          checkbox: true,
          checked: true,
          expanded: true,
          items: this.designData.stencils.map(item => ({
            id: `${item.id}${ITEM_COMPOSE_ID_SEPARATOR}${DESIGN_DATA_TYPE_STENCILS}`,
            class: "object tree-view-item",
            leaf: !item.warning,
            caption: item.name,
            startImgSrc: "objects/stencil",
            checkbox: true,
            checked: true,
            expanded: true,
            items: item.warning && this.#warningTreeItem(item.warning)
          }))
        },
        {
          order: 2,
          id: `parent${ITEM_COMPOSE_ID_SEPARATOR}${DESIGN_DATA_TYPE_IMAGES}`,
          leaf: false,
          caption: this.#componentLocale.tree.images,
          startImgSrc: imagesIconSrc,
          checkbox: true,
          checked: true,
          expanded: true,
          items: this.#recursiveDesignTreeItems(
            this.designData.images,
            DESIGN_DATA_TYPE_IMAGES,
            imagesIconSrc
          )
        },
        {
          order: 3,
          id: `parent${ITEM_COMPOSE_ID_SEPARATOR}${DESIGN_DATA_TYPE_FONTS}`,
          leaf: false,
          caption: this.#componentLocale.tree.fonts,
          startImgSrc: "controls/font",
          checkbox: true,
          checked: true,
          expanded: true,
          items: this.designData.fonts.map(item => ({
            id: `${item.id}${ITEM_COMPOSE_ID_SEPARATOR}${DESIGN_DATA_TYPE_FONTS}`,
            leaf: !item.warning,
            caption: item.name,
            startImgSrc: "controls/font",
            checkbox: true,
            checked: true,
            expanded: true,
            items: item.warning && this.#warningTreeItem(item.warning)
          }))
        },
        {
          order: 4,
          id: `${
            this.designData.designSystem.length > 0
              ? this.designData.designSystem[0].id
              : "parent"
          }${ITEM_COMPOSE_ID_SEPARATOR}${DESIGN_DATA_TYPE_DESIGN_SYSTEM}`,
          leaf: true,
          caption: this.#componentLocale.tree.designSystem,
          startImgSrc: "objects/dso",
          checkbox: true,
          checked: true
        }
      ];
    }
  }

  /**
   * The model data loaded into the detail tree
   */
  @State() detailTreeModel: TreeViewItemModel[] = [];

  /**
   * If the design data is loading or not, for disabled actions
   */
  @State() isLoading = false;

  /**
   * If the design data is loaded or not, for conditioning input file
   */
  @State() isLoaded = false;

  /**
   * The messages receive when the file is loading
   */
  @State() messages: MessageData[] = [];

  /**
   * The file selected for load the design data
   */
  @State() selectedFile: File;

  /**
   * The type of the design data selected for show it visual tabs
   */
  @State() selectedDesignType: DesignType;

  /**
   * The item of the design data selected for show its contents on visual tabs
   */
  @State() selectedDesignItem: any;

  /**
   * Whether to show or not the access token input field
   */
  @State() requiresAccessToken: boolean;

  /**
   * the design data tree view data model
   */
  @State() designDataTreeDataModel: TreeViewItemModel[] = [];

  /**
   * Used to trigger a re-render after an item has been selected on
   * renderDesignTree, in order to set the first tab as the active tab
   * (setFirstActiveTab()), since calling this method inside
   * objectsTreeSelectedItemsChangedHandler does not always work without a
   * setTimeOut.
   */
  @State() updateTabsActiveTab: boolean = false;

  /**
   * If true, images will be fitted in the container
   */
  @State() fitImages = false;

  /**
   * Represents a collection of settings where each key is a specific option. Its
   * boolean value determines whether changes to that option are listened to and
   * trigger the loadCallback callback.
   */
  @Prop() readonly reactiveSettings: ReactiveSettings = {
    path: true,
    style: false,
    module: false,
    importAsWebPanels: true
  };

  /**
   * Used to keep track of the settings values since the last loadCallback.
   * Used to know if loadCallback is required to be called again, when any
   * of these values are changed.
   */
  @State() lastLoadCallbackSettingValues: ReactiveSettingsValues = {
    path: undefined,
    style: undefined,
    moduleId: undefined,
    importAsWebPanels: undefined
  };

  /**
   * The valid extensions for the files that will be loaded in Path/URL
   * (ie.: ".fig, .sketch")
   */
  @Prop() readonly extensions!: string[];

  /**
   * Default value for the 'Module' field.
   */
  @Prop() readonly defaultModule: EntityData;

  /**
   * The path/URL for load the design data
   */
  @Prop({ mutable: true }) path: string = "";
  @Watch("path")
  pathChanged(newPath: string) {
    const fileIsLoaded = this.selectedFile?.size > 0;
    const validatePathResult = this.#validatePath(newPath) || fileIsLoaded;
    this.pathIsValid = validatePathResult;
  }

  /**
   * The access token for the given path, which is not always required
   */
  @Prop({ mutable: true }) accessToken: string = "";
  @Watch("accessToken")
  accessTokenChanged(newAccessToken: string) {
    if (!newAccessToken.trim().length) {
      this.accessTokenHasError = true;
      return;
    }
    this.accessTokenHasError = false;
    this.accessToken = newAccessToken;
  }

  /**
   * The value for "Import as Web Panels" checkbox.
   */
  @Prop() readonly importAsWebPanels: boolean = false;

  /**
   * Callback invoked when the action is executed in the Module/Folder filter.
   * It returns the information of the selected object (id and name) or
   * 'undefined' if it was canceled.
   */
  @Prop() readonly selectModuleCallback: SelectModuleCallback;

  /**
   * This is a function provided by the developer that cancel the load operation.
   */
  @Prop() readonly cancelCallback!: CancelCallback;

  /**
   * This is a function provided by the developer that initiates the process
   * of importing a design.
   */
  @Prop() readonly confirmCallback!: ConfirmCallback;

  /**
   * This is a function provided by the developer that returns the list of
   * data types loaded from a file or URL.
   */
  @Prop() readonly loadCallback!: LoadCallback;

  /**
   * This is a function provided by the developer that returns the information
   * of a Design System type of data.
   */
  // eslint-disable-next-line prettier/prettier
  @Prop() readonly loadDesignSystemCallback!: LoadDesignSystemCallback;

  /**
   * This is a function provided by the developer that returns the information
   * of a Font type of data.
   */
  @Prop() readonly loadFontCallback!: LoadFontCallback;

  /**
   * This is a function provided by the developer that returns the information
   * of an Image System type of data.
   */
  @Prop() readonly loadImageCallback!: LoadImageCallback;

  /**
   * This is a function provided by the developer that returns the information
   * of a Panel type of data.
   */
  @Prop() readonly loadPanelDataCallback!: LoadPanelDataCallback;

  /**
   * Callback invoked to determine if accessing a design requires an authentication token.
   */
  @Prop() readonly requiresAccessTokenCallback!: RequiresAccessTokenCallback;

  /**
   * The styles render in the option style selector
   */
  @Prop() readonly styles!: GxOption[];
  @Watch("styles")
  stylesChanged(newStyles: GxOption[]) {
    this.stylesAsComboBoxModel = mapStylesToComboBoxItemModel(newStyles);
  }

  /**
   * This is a function provided by the developer for create an instance of a text editor.
   */
  // eslint-disable-next-line prettier/prettier
  @Prop() readonly textEditorFactoryCallback!: TextEditorFactoryCallback;

  async componentWillLoad() {
    this.#componentLocale = await Locale.getComponentStrings(this.el);
    this.#formattedExtensions = this.extensions
      .map(ext => `.${ext}`)
      .join(", ");
    this.stylesChanged(this.styles);
    this.#initializeActiveTabs();
    this.pathChanged(this.path);
    this.styleValue =
      this.stylesAsComboBoxModel?.length > 0
        ? this.stylesAsComboBoxModel[0]?.value
        : "";
  }

  @Watch("messages")
  watchMessagesHandler(newValue: boolean, oldValue: boolean) {
    if (newValue !== oldValue) {
      if (this.#messagesList) {
        this.#messagesList.scrollTop = this.#messagesList.scrollHeight;
      }
    }
  }

  #countDesignDataItems = (tree: DesignItemData[]) => {
    let count = 0;

    function countItemsRecursively(designItemsArray: DesignItemData[]) {
      for (const designItem of designItemsArray) {
        count++;
        if (designItem.variants && Array.isArray(designItem.variants)) {
          countItemsRecursively(designItem.variants);
        }
      }
    }

    countItemsRecursively(tree);

    return count;
  };

  #warningTreeItem = (caption: string) => {
    return [
      {
        leaf: true,
        caption: `Warning: ${caption}`,
        startImgSrc: getIconPath({
          category: "system",
          name: "warning",
          colorType: "warning"
        }),
        order: -1,
        checkbox: false,
        disabled: true
      } as TreeViewItemModel
    ];
  };

  #recursiveDesignTreeItems(
    items: DesignItemData[],
    designType: DesignType,
    iconSrc: string,
    selectedIndex: number = undefined
  ): TreeViewItemModel[] {
    return items.map((item, i) => {
      const id = `${item.id}${ITEM_COMPOSE_ID_SEPARATOR}${designType}`;
      const hasVariants = item.variants?.length > 0;
      const itemWithWarning = item.warning
        ? this.#warningTreeItem(item.warning)
        : [];
      const isLeaf = !hasVariants && !item.warning;

      return {
        id: id,
        leaf: isLeaf,
        caption: item.name,
        startImgSrc: iconSrc,
        checkbox: true,
        checked: true,
        expanded: !isLeaf,
        selected: selectedIndex === i,
        order: i,
        items: [
          ...itemWithWarning,
          ...(hasVariants
            ? this.#recursiveDesignTreeItems(item.variants, designType, iconSrc)
            : [])
        ]
      };
    });
  }

  #initializeActiveTabs = () => {
    this.activeTabs.set("panels", TAB_MODEL.panels[0].id);
    this.activeTabs.set("stencils", TAB_MODEL.stencils[0].id);
    this.activeTabs.set("images", TAB_MODEL.images[0].id);
    this.activeTabs.set("fonts", TAB_MODEL.fonts[0].id);
    this.activeTabs.set("design_system", TAB_MODEL.designSystem[0].id);
  };

  #evaluateAccessTokenRender = async () => {
    this.requiresAccessToken = await this.requiresAccessTokenCallback(
      this.path
    );
    // evaluate access token error
    if (this.accessToken.length) {
      this.accessTokenHasError = false;
    }
  };

  #pathURLBlurHandler = async () => {
    const loadDataCondition1 =
      this.isLoaded &&
      this.reactiveSettings.path &&
      this.pathIsValid &&
      this.path !== this.lastLoadCallbackSettingValues.path;
    const loadDataCondition2 =
      !this.requiresAccessToken ||
      (this.requiresAccessToken && this.accessToken.length > 0);
    if (loadDataCondition1 && loadDataCondition2) {
      this.#loadData();
    }
  };

  #loadTreeItemCompositionModel(
    controls: DesignPanelControlData[]
  ): TreeViewItemModel[] {
    const modelList: TreeViewItemModel[] = [];
    for (const control of controls) {
      let model: TreeViewItemModel = {
        id: control.name,
        caption: control.name,
        startImgSrc: control.type,
        checkbox: false,
        expanded: true,
        leaf: control.leaf
      };
      if (control.controls) {
        model = {
          ...model,
          items: this.#loadTreeItemCompositionModel(control.controls)
        };
      }
      modelList.push(model);
    }
    return modelList;
  }

  #objectsTreeSelectedItemsChangedHandler = async (
    event: CustomEvent<TreeViewItemModelExtended[]>
  ) => {
    if (event.detail.length) {
      const item = event.detail[0]?.item;
      const itemComposedId = item.id;
      const idList = itemComposedId.split(ITEM_COMPOSE_ID_SEPARATOR);
      const itemId = idList[0];
      const itemDesignType: DesignType = idList[1] as DesignType;
      const isChildNode = itemId && itemId !== "parent";
      const isDesignSystem = itemDesignType === "design_system";
      if (isChildNode || isDesignSystem) {
        this.tabsModel = DESIGN_TYPE_TO_TAB_MODEL_MAP.get(itemDesignType);
        this.selectedDesignType = itemDesignType;
        const loadedItem = this.#getLoadedItem(itemComposedId);
        if (!loadedItem) {
          const designDataDetail: DesignItemDataDetail =
            await this.#getDesignDataDetail(itemId, this.selectedDesignType);
          this.#loadedItems.set(itemComposedId, {
            designType: this.selectedDesignType,
            designDataDetail: designDataDetail
          });
        } else {
          // Item has been queried already. The item content exists already.
          this.selectedDesignType = loadedItem.designType;
          this.selectedDesignItem = loadedItem.designDataDetail;
          this.#getCodeFactory(loadedItem.designType);
          this.#renderDetailDataTree(loadedItem.designType);
        }
        this.renderedTabs.clear();
        this.renderedTabs.add(this.activeTabs.get(this.selectedDesignType));
      }
    }
  };

  #getLoadedItem = (id: string): ItemLoadedInfo | undefined => {
    return this.#loadedItems?.get(id);
  };

  #validatePath = (path: string): boolean => {
    const validPathRegex = /^(\/|\.\/|(\w:))[^:*?"<>|]*\.(sketch|gxsketch)$/i; // Match valid file paths
    const figmaURLRegex = /^https?:\/\/(www\.)?figma\.com/; // Match URLs containing "www.figma.com"
    const urlWithExtensionRegex =
      /^https?:\/\/[^\s/$.?#].[^\s]*\.(sketch|gxsketch)$/i; // Match URLs ending with .sketch or .gxsketch
    const pathIsValid =
      validPathRegex.test(path) ||
      figmaURLRegex.test(path) ||
      urlWithExtensionRegex.test(path);
    return pathIsValid;
  };

  #objectsTreeCheckedItemsChangedHandler = (
    event: CustomEvent<Map<string, TreeViewItemModelExtended>>
  ) => {
    const items = Array.from(event.detail.values());
    let currentParentId = null;
    const result: { [key: string]: string[] } = {};

    for (const item of items) {
      if (item.item.id.startsWith(`parent${ITEM_COMPOSE_ID_SEPARATOR}`)) {
        currentParentId = item.item.id.split(ITEM_COMPOSE_ID_SEPARATOR)[1];
        result[currentParentId] = [];
      } else if (item.item.checked && currentParentId) {
        const parentId = item.item.id.split(ITEM_COMPOSE_ID_SEPARATOR)[1];
        if (parentId === currentParentId) {
          const id = item.item.id.split(ITEM_COMPOSE_ID_SEPARATOR)[0];
          result[currentParentId].push(id);
        }
      }
    }
    // const { designSystem, fonts, images, panels, stencils } = result;
    const { fonts, panels, stencils, images } = result;

    // this.designSystemSelected = designSystem;
    this.#panelsSelected = panels;
    this.#stencilsSelected = stencils;
    this.#imagesSelected = images;
    this.#fontsSelected = fonts;
  };

  #selectFileHandler = (): void => {
    this.#fileInputHiddenEl.click();
  };

  #styleComboBoxInputHandler = (event: CustomEvent<string> | InputEvent) => {
    this.styleValue = (event.target as HTMLChComboBoxRenderElement).value;
    if (
      this.reactiveSettings.style &&
      this.styleValue !== this.lastLoadCallbackSettingValues.style
    ) {
      this.#loadData();
    }
  };

  #moduleInputHandler = (event: CustomEvent<EntityData>) => {
    if (
      this.reactiveSettings.module &&
      this.defaultModule.id !== event.detail.id &&
      this.pathIsValid
    ) {
      this.#loadData();
    }
  };

  #loadData = async (): Promise<void> => {
    this.isLoading = true;
    const source = this.selectedFile ? this.selectedFile : this.path;
    const token = this.#accessTokenEl ? this.#accessTokenEl.value : undefined;
    const style = this.styleValue;
    const importAsWebPanels = this.#importAsWebPanelsEl.value === CHECKED_VALUE;
    const designData: DesignData = await this.loadCallback(
      source,
      style,
      importAsWebPanels,
      token,
      this.#messageCallback
    );

    // clear
    this.#panelsSelected = [];
    this.#stencilsSelected = [];
    this.#fontsSelected = [];
    this.selectedDesignType = undefined;
    this.selectedDesignItem = undefined;
    this.messages = [];

    this.designData = designData;
    this.isLoaded = designData?.success ?? false;
    this.isLoading = false;

    const firstDesignSystemName = designData.designSystem[0]?.name;
    if (firstDesignSystemName && !this.styleValue) {
      this.styleValue = firstDesignSystemName;
    }

    // update the settings that were used for the last load
    if (this.reactiveSettings.path && this.pathIsValid) {
      this.lastLoadCallbackSettingValues.path = this.path;
    }
    if (this.reactiveSettings.style) {
      this.lastLoadCallbackSettingValues.style = style;
    }
    if (this.reactiveSettings.module) {
      this.lastLoadCallbackSettingValues.moduleId = this.#moduleEl.value.id;
    }
    if (this.reactiveSettings.importAsWebPanels) {
      this.lastLoadCallbackSettingValues.importAsWebPanels = importAsWebPanels;
    }
  };

  #messageCallback: LoadMessageCallback = message => {
    this.messages = [...this.messages, message];
  };

  #getDesignDataDetail = async (
    id: string,
    designType: DesignType
  ): Promise<DesignItemDataDetail> => {
    let loadFn:
      | LoadPanelDataCallback
      | LoadImageCallback
      | LoadFontCallback
      | LoadDesignSystemCallback;

    switch (designType) {
      case DESIGN_DATA_TYPE_IMAGES:
        loadFn = this.loadImageCallback;
        break;
      case DESIGN_DATA_TYPE_DESIGN_SYSTEM:
        loadFn = this.loadDesignSystemCallback;
        break;
      case DESIGN_DATA_TYPE_FONTS:
        loadFn = this.loadFontCallback;
        break;
      case DESIGN_DATA_TYPE_PANELS:
      case DESIGN_DATA_TYPE_STENCILS:
        loadFn = this.loadPanelDataCallback;
        break;
    }

    let returnedData: DesignItemDataDetail;
    await loadFn(id).then((data: DesignItemDataDetail) => {
      this.selectedDesignType = designType;
      this.selectedDesignItem = data;
      this.#getCodeFactory(designType);
      this.#renderDetailDataTree(designType);
      returnedData = data;
    });

    return returnedData;
  };

  #getCodeFactory(designType: string) {
    switch (designType) {
      case DESIGN_DATA_TYPE_PANELS:
      case DESIGN_DATA_TYPE_STENCILS:
        this.textEditorFactoryCallback(
          "code",
          TAB_DATA.code.slot,
          this.selectedDesignItem.code
        );
        this.textEditorFactoryCallback(
          "html",
          TAB_DATA.layout.slot,
          this.selectedDesignItem.layout
        );
        break;
      case DESIGN_DATA_TYPE_DESIGN_SYSTEM:
        this.textEditorFactoryCallback(
          "tokens",
          TAB_DATA.tokens.slot,
          this.selectedDesignItem.tokens
        );
        this.textEditorFactoryCallback(
          "styles",
          TAB_DATA.styles.slot,
          this.selectedDesignItem.styles
        );
        break;
    }
  }

  #evaluateDesignItemsChecked = (
    designItemsData: DesignItemData[],
    selectedItems: string[]
  ) => {
    let itemsToImport: string[] = selectedItems;

    const totalItemsInTree = this.#countDesignDataItems(designItemsData);
    const selectedItemsLength = selectedItems.length;
    if (totalItemsInTree === selectedItemsLength) {
      itemsToImport = this.#objectTreeCheckedStatus.all;
    } else if (selectedItemsLength === 0) {
      itemsToImport = this.#objectTreeCheckedStatus.none;
    }

    return itemsToImport;
  };

  #confirm = (): void => {
    const panelsToImport = this.#evaluateDesignItemsChecked(
      this.designData.panels,
      this.#panelsSelected
    );
    const stencilsToImport = this.#evaluateDesignItemsChecked(
      this.designData.stencils,
      this.#stencilsSelected
    );
    const imagesToImport = this.#evaluateDesignItemsChecked(
      this.designData.images,
      this.#imagesSelected
    );
    const fontsToImport = this.#evaluateDesignItemsChecked(
      this.designData.fonts,
      this.#fontsSelected
    );

    this.confirmCallback({
      panels: panelsToImport,
      stencils: stencilsToImport,
      images: imagesToImport,
      fonts: fontsToImport,
      moduleId: this.#moduleEl?.value?.id,
      styleName: this.styleValue,
      importAsWebPanels: this.#importAsWebPanelsEl.value === CHECKED_VALUE
    });
  };

  #cancel = (): void => {
    this.cancelCallback();
    this.isLoading = false;
  };

  #fitImagesHandler = (event: ChCheckboxCustomEvent<any> | InputEvent) => {
    this.fitImages = event.detail === CHECKED_VALUE;
  };

  #getPanelsSelectedItem = (panels: DesignItemData[]): number => {
    return panels.reduce((minIndex, item, index, panels) => {
      return panels[minIndex].name <= item.name ? minIndex : index;
    }, 0);
  };

  #importAsWebPanelsChangedHandler = () => {
    const loadData =
      this.reactiveSettings.importAsWebPanels && this.pathIsValid;
    if (loadData) {
      this.#loadData();
    }
  };

  #removeFileSelection = () => {
    this.selectedFile = null;
    this.path = "";
    this.accessToken = "";
    this.selectedFile = null;
    this.#fileInputHiddenEl.value = null;
  };

  #selectFileInputChangedHandler = (event: InputEvent) => {
    const target = event.target as HTMLInputElement;
    if (target.files?.length > 0) {
      this.selectedFile = target.files[0];
      this.path = this.selectedFile.name;
      // we know the path is valid because the extensions are restricted by "extensions" Prop.
    }
  };

  #accessFocusHandler = (event: ChEditCustomEvent<string> | FocusEvent) => {
    const element = event.target as HTMLChEditElement;
    element.type = "text"; // display token on focus
  };

  #accessBlurHandler = (event: ChEditCustomEvent<string> | FocusEvent) => {
    const element = event.target as HTMLChEditElement;
    element.type = "password"; // hide token on blur

    this.accessTokenChanged(element.value);
  };

  #pathUrlInputHandler = async (
    event: ChEditCustomEvent<string> | InputEvent
  ) => {
    this.path = event.detail as string;
    this.#evaluateAccessTokenRender();
    this.selectedFile = null;
    if (!this.path.length) {
      this.designData = null;
    }
  };

  #renderHeader = (): Element => {
    const primaryButtonDisabled =
      this.isLoading ||
      (this.pathIsValid &&
        this.requiresAccessToken &&
        this.accessToken.length === 0);
    const clearFileButtonDisabled = this.isLoading || this.path.length === 0;
    const stylesComboDisabled = this.isLoading;
    const moduleDisabled = this.isLoading;
    const pathWithErrorStyles = !this.pathIsValid && this.path.length > 0;
    const displayPathErrorTooltip = !this.pathIsValid && this.path.length > 0;
    const fitImagesIsDisabled =
      this.isLoading ||
      !this.designDataTreeDataModel.length ||
      this.selectedDesignType === "stencils" ||
      this.selectedDesignType === "design_system";

    let primaryButtonCaption;
    if (
      this.pathIsValid &&
      this.path === this.lastLoadCallbackSettingValues.path
    ) {
      primaryButtonCaption = this.#componentLocale.header.btnReload;
    } else if (this.pathIsValid) {
      primaryButtonCaption = this.#componentLocale.header.btnLoad;
    } else {
      primaryButtonCaption = this.#componentLocale.header.selectFileButton;
    }

    return (
      <header
        class={{
          "header": true,
          "dialog-header-with-border": true,
          "spacing-body-inline": true,
          "spacing-body-block-start": true,
          "header--show-access-token": this.requiresAccessToken,
          "header--hide-access-token": !this.requiresAccessToken,
          "field-group-justified-start": true
        }}
      >
        <label
          // path/url label
          class="label path-url-label"
        >
          {this.#componentLocale.header.path}
        </label>
        <div class="path-url-wrapper buttons-spacer">
          <ch-edit
            // path/url/file input
            id="path-url"
            class={{
              "input": true,
              "input-error": pathWithErrorStyles,
              "path-url-control": true
            }}
            debounce={config.inputDebounce}
            part="option-path"
            type="text"
            value={this.path}
            onInput={this.#pathUrlInputHandler}
            onBlur={this.reactiveSettings.path && this.#pathURLBlurHandler}
            disabled={this.isLoading}
            placeholder={`${this.#componentLocale.header.pathPlaceholder}`}
            startImgSrc={FILE_UPLOAD}
            ref={(el: HTMLChEditElement) =>
              (this.#pathEl = el as HTMLChEditElement)
            }
          ></ch-edit>
          {displayPathErrorTooltip && (
            <ch-tooltip
              class="tooltip"
              actionElement={this.#pathEl as unknown as HTMLButtonElement}
              blockAlign={config.tooltipSettings.blockAlign}
              inlineAlign={config.tooltipSettings.inlineAlign}
              delay={config.tooltipSettings.delay}
            >
              {this.#componentLocale.header.pathErrorMessage}{" "}
              {this.#formattedExtensions}
            </ch-tooltip>
          )}

          <input
            hidden
            type="file"
            accept={this.#formattedExtensions}
            onChange={this.#selectFileInputChangedHandler}
            ref={(el: HTMLInputElement) =>
              (this.#fileInputHiddenEl = el as HTMLInputElement)
            }
          />

          <div class="buttons-spacer">
            <button
              // remove file selection
              aria-label={this.#componentLocale.header.removeFileSelection}
              title={this.#componentLocale.header.removeFileSelection}
              id="reset-all-button"
              class="button-tertiary button-icon-only"
              part="reset-all-button"
              disabled={clearFileButtonDisabled}
              onClick={!clearFileButtonDisabled && this.#removeFileSelection}
            >
              <ch-image
                class="icon-m"
                src={MENU_DELETE}
                disabled={clearFileButtonDisabled}
              ></ch-image>
            </button>
            <button
              // load file button
              id="button-load"
              type="primary-text-only"
              onClick={
                this.pathIsValid ? this.#loadData : this.#selectFileHandler
              }
              part="button button--load"
              disabled={primaryButtonDisabled}
              class={{
                "button-primary": this.pathIsValid,
                "button-secondary": !this.pathIsValid,
                "select-load-button": true
              }}
            >
              {primaryButtonCaption}
            </button>
          </div>
        </div>

        {this.requiresAccessToken && [
          <label
            // access token label
            class="label access-token-label"
          >
            {this.#componentLocale.header.accessToken}
          </label>,
          <div>
            <ch-edit
              class={{
                "input": true,
                "access-token": true,
                "input-error": this.accessTokenHasError
              }}
              ref={(el: HTMLChEditElement) =>
                (this.#accessTokenEl = el as HTMLChEditElement)
              }
              value={this.accessToken}
              placeholder={this.#componentLocale.header.accessTokenPlaceholder}
              onFocus={this.#accessFocusHandler}
              onBlur={this.#accessBlurHandler}
              part="access-token"
              disabled={this.isLoading}
              type="password"
            ></ch-edit>
            {this.accessTokenHasError && (
              <ch-tooltip
                class="tooltip"
                actionElement={
                  this.#accessTokenEl as unknown as HTMLButtonElement
                }
                blockAlign={config.tooltipSettings.blockAlign}
                inlineAlign={config.tooltipSettings.inlineAlign}
                delay={config.tooltipSettings.delay}
              >
                {this.#componentLocale.header.accessTokenErrorMessage}
              </ch-tooltip>
            )}
          </div>
        ]}
        <label
          // style label
          class="label style-label"
        >
          {this.#componentLocale.header.style}
        </label>
        <ch-combo-box-render
          // style combo box
          suggest={true}
          suggestOptions={{
            alreadyProcessed: true
          }}
          disabled={stylesComboDisabled}
          model={this.stylesAsComboBoxModel}
          value={this.styleValue}
          onInput={this.#styleComboBoxInputHandler}
          part="option-style"
          class="combo-box style"
        ></ch-combo-box-render>

        <label
          // module label
          class="label module-label"
        >
          {this.#componentLocale.header.module}
        </label>
        <gx-ide-entity-selector
          // module entity selector
          labelPosition="none"
          value={this.defaultModule}
          defaultValue={this.defaultModule}
          selectEntityCallback={this.selectModuleCallback}
          ref={(el: HTMLGxIdeEntitySelectorElement) =>
            (this.#moduleEl = el as HTMLGxIdeEntitySelectorElement)
          }
          onValueChanged={
            this.reactiveSettings.module && this.#moduleInputHandler
          }
          disabled={moduleDisabled}
          class="module"
        ></gx-ide-entity-selector>

        <div
          // import as web panels
          class="checkboxes-options field-group"
        >
          <ch-checkbox
            caption={this.#componentLocale.header.importAs}
            checkedValue={CHECKED_VALUE}
            class="checkbox import-as-web-panels"
            ref={(el: HTMLChCheckboxElement) =>
              (this.#importAsWebPanelsEl = el as HTMLChCheckboxElement)
            }
            value={this.importAsWebPanels ? CHECKED_VALUE : undefined}
            part="option-import-as-web-panels"
            disabled={this.isLoading}
            onInput={this.#importAsWebPanelsChangedHandler}
          ></ch-checkbox>

          <div
            class={{
              "field-group-inline": true,
              "checkboxes-options__col-right": true
            }}
          >
            <ch-checkbox
              // fit images
              caption={this.#componentLocale.header.fitImages}
              checkedValue={CHECKED_VALUE}
              class="checkbox"
              part="fit-images-checkbox"
              disabled={fitImagesIsDisabled}
              onInput={this.#fitImagesHandler}
            ></ch-checkbox>
          </div>
        </div>
      </header>
    );
  };

  #renderMessages = (): HTMLParagraphElement[] => {
    return (
      this.messages.length > 0 &&
      this.messages.map(message => (
        <p
          class={{
            "body-regular-m": true,
            "message": true,
            [`message--${message.type}`]: true
          }}
        >
          {message.type}: {message.text}
        </p>
      ))
    );
  };

  #renderDetailDataTree(designType: string) {
    switch (designType) {
      case DESIGN_DATA_TYPE_PANELS:
      case DESIGN_DATA_TYPE_STENCILS:
        this.detailTreeModel = this.#loadTreeItemCompositionModel(
          this.selectedDesignItem.composition.controls
        );
        break;
    }
  }

  #renderMain = () => {
    if (this.isLoading || this.designData?.success === false) {
      return (
        <div
          class="main main--loading scrollable"
          ref={(el: HTMLElement) => (this.#messagesList = el as HTMLElement)}
        >
          {this.#renderMessages()}
          <div class="body-regular-m spacing-body">
            <slot name="messages"></slot>
          </div>
        </div>
      );
    } else if (this.designData?.success) {
      return (
        /* #main*/
        <div class="main">
          {[this.#renderDesignTree(), this.#renderDesignTabsCh()]}
        </div>
      );
    } else {
      return (
        <gx-ide-empty-state
          class="empty-state scrollable"
          stateIconSrc={DESIGN}
          isAnimated={this.#emptyStateAnimated}
          stateTitle={this.#componentLocale.messages.noDesignSelected}
          key="no-objects-empty-state"
        >
          <button
            // begin by selecting a file
            class="button-tertiary empty-state-button"
            onClick={this.#selectFileHandler}
          >
            {this.#componentLocale.messages.selectDesign}
          </button>
        </gx-ide-empty-state>
      );
    }
  };

  #renderDesignTree = (): HTMLChTreeViewElement => {
    return (
      <ch-tree-view-render
        part="data-tree"
        class="tree-view"
        id="data-tree"
        showLines="last"
        model={this.designDataTreeDataModel}
        dragDisabled={true}
        dropDisabled={true}
        expandableButton="action"
        expandOnClick={false}
        toggleCheckboxes={true}
        checkbox={true}
        checked={true}
        onCheckedItemsChange={this.#objectsTreeCheckedItemsChangedHandler}
        onSelectedItemsChange={this.#objectsTreeSelectedItemsChangedHandler}
      ></ch-tree-view-render>
    );
  };

  #renderFooter = () => {
    const buttonOkDisabled =
      this.designData?.success !== true || this.isLoading;
    return (
      <footer class="dialog-footer-with-border spacing-body-block-end spacing-body-inline">
        <div class="buttons-spacer">
          <button
            // button cancel
            id="button-cancel"
            class="button-secondary button-cancel"
            type="outlined"
            onClick={this.#cancel}
            part="button button--cancel"
            slot="footer-end"
          >
            {this.#componentLocale.footer.btnCancel}
          </button>
          <button
            // button ok
            id="button-ok"
            class="button-primary button-ok"
            type="primary-text-only"
            onClick={!buttonOkDisabled && this.#confirm}
            part="button button--ok"
            disabled={buttonOkDisabled}
            slot="footer-end"
          >
            {this.#componentLocale.footer.btnOk}
          </button>
        </div>
      </footer>
    );
  };

  #evaluatePanelPreview = () => {
    if (this.selectedDesignType === "panels") {
      return this.selectedDesignItem?.composition?.preview ? (
        <div
          part="data-panel-composition-preview"
          class="panel-item scrollable"
        >
          <img
            src={this.selectedDesignItem.composition.preview}
            alt="Preview"
          />
        </div>
      ) : (
        <gx-ide-empty-state
          class="panel-tab__empty-state"
          stateTitle={this.#componentLocale.messages.noPanelPreviewAvailable}
          stateIconSrc={
            this.#importAsWebPanelsEl.value === CHECKED_VALUE
              ? WEBPANEL_ICON
              : PANEL_FOR_SD_ICON
          }
        ></gx-ide-empty-state>
      );
    }
    return null;
  };

  #renderPanelTab = (): Element => {
    const renderControlsTree =
      (this.selectedDesignType === DESIGN_DATA_TYPE_PANELS ||
        this.selectedDesignType === DESIGN_DATA_TYPE_STENCILS) &&
      this.detailTreeModel.length > 0;

    return (
      <div
        class={{
          "panel-tab": true,
          "panel-tab--controls-tree": renderControlsTree
        }}
        part="data-panel-composition-container"
      >
        {this.#evaluatePanelPreview()}

        {renderControlsTree ? (
          <ch-tree-view-render
            part="data-panel-composition-tree"
            class="tree-view tree-view-panel-item scrollable"
            showLines="last"
            model={this.detailTreeModel}
            dragDisabled={true}
            dropDisabled={true}
          ></ch-tree-view-render>
        ) : null}
      </div>
    );
  };

  #renderImageTab = () => {
    return (
      <div
        part="data-panel-composition-preview"
        class="data-preview"
        style={{ height: "100%" }}
      >
        <img
          src={this.selectedDesignItem ? this.selectedDesignItem : undefined}
          alt="Preview"
          class={
            this.selectedDesignType === DESIGN_DATA_TYPE_IMAGES
              ? "checker-board"
              : undefined
          }
        />
      </div>
    );
  };

  #tabSelectionChangedHandler = (
    event: CustomEvent<{
      lastSelectedIndex: number;
      newSelectedId: string;
      newSelectedIndex: number;
    }>
  ): void => {
    this.renderedTabs.clear();
    this.renderedTabs.add(event.detail.newSelectedId);
    this.reRenderCounter++;
    this.activeTabs.set(this.selectedDesignType, event.detail.newSelectedId);
  };

  #renderDesignTabsCh = (): HTMLChTabRenderElement => {
    return (
      <ch-tab-render
        // main section
        class={{
          "tab": true,
          "scrollable": true,
          "fit-images": this.fitImages
        }}
        tabListPosition="block-start"
        model={this.tabsModel}
        selectedId={this.activeTabs.get(this.selectedDesignType)}
        overflow="auto"
        contain="size"
        onSelectedItemChange={this.#tabSelectionChangedHandler}
      >
        <div
          slot={TAB_DATA.tokens.slot}
          class="spacing-body"
          style={{
            display: this.renderedTabs.has(TAB_DATA.tokens.id) ? "" : "none"
          }}
        >
          <slot name={TAB_DATA.tokens.slot}></slot>
        </div>

        <div
          slot={TAB_DATA.styles.slot}
          class="spacing-body"
          style={{
            display: this.renderedTabs.has(TAB_DATA.styles.id) ? "" : "none"
          }}
        >
          <slot name={TAB_DATA.styles.slot}></slot>
        </div>

        <div
          class="composition-tab-slot scrollable"
          slot={TAB_DATA.composition.slot}
          style={{
            display: this.renderedTabs.has(TAB_DATA.composition.id)
              ? ""
              : "none"
          }}
        >
          {this.selectedDesignType === DESIGN_DATA_TYPE_IMAGES ||
          this.selectedDesignType === DESIGN_DATA_TYPE_FONTS
            ? this.#renderImageTab()
            : this.#renderPanelTab()}
        </div>

        <div
          slot={TAB_DATA.layout.slot}
          class="spacing-body"
          style={{
            display: this.renderedTabs.has(TAB_DATA.layout.id) ? "" : "none"
          }}
        >
          <slot name={TAB_DATA.layout.slot}></slot>
        </div>

        <div
          slot={TAB_DATA.code.slot}
          class="spacing-body"
          style={{
            display: this.renderedTabs.has(TAB_DATA.code.id) ? "" : "none"
          }}
        >
          <slot name={TAB_DATA.code.slot}></slot>
        </div>
      </ch-tab-render>
    );
  };

  render(): void {
    return (
      <Host class="widget">
        <ch-theme model={CSS_BUNDLES}></ch-theme>
        <section class="section">
          {this.#renderHeader()}
          {this.#renderMain()}
          {this.#renderFooter()}
        </section>
      </Host>
    );
  }
}

export type LoadMessageCallback = (message: MessageData) => void;
export type LoadCallback = (
  source: string | File,
  designSystem: string,
  importAsWebPanels: boolean,
  token: string | undefined,
  onMessage: LoadMessageCallback
) => Promise<DesignData | undefined>;
export type LoadPanelDataCallback = (id: string) => Promise<DesignPanelData>;
export type LoadImageCallback = (id: string) => Promise<string>;
export type LoadFontCallback = (id: string) => Promise<string>;
export type LoadDesignSystemCallback = (
  id: string
) => Promise<DesignSystemData>;
export type TextEditorFactoryCallback = (
  type: TextEditorType,
  parent: string,
  text: string
) => Promise<void>;
export type SelectModuleCallback = () => Promise<EntityData | undefined>;

export type RequiresAccessTokenCallback = (source: string) => Promise<boolean>;
export type ConfirmCallback = (data: DesignImportData) => Promise<boolean>;
export type CancelCallback = () => Promise<void>;

export interface MessageData {
  type: "error" | "warning" | "info";
  text: string;
}

export interface DesignData {
  panels: DesignItemData[];
  stencils: DesignItemData[];
  images: DesignItemData[];
  designSystem: DesignItemData[];
  fonts: DesignItemData[];
  success: boolean;
}

export interface DesignItemData {
  id: string;
  name: string;
  warning?: string;
  variants?: DesignItemData[];
}

export interface DesignPanelData {
  composition: DesignPanelCompositionData;
  code?: string;
  layout?: string;
}

export interface DesignPanelCompositionData {
  preview?: string;
  controls: DesignPanelControlData[];
}

export interface DesignPanelControlData {
  name: string;
  type: string;
  leaf?: boolean;
  controls: DesignPanelControlData[];
}

export interface DesignSystemData {
  tokens: string;
  styles: string;
}

type DesignItemDataDetail = DesignPanelData | DesignSystemData | string;

export interface DesignImportData {
  panels: string[];
  stencils: string[];
  fonts: string[];
  moduleId: string;
  images: string[];
  styleName: string;
  importAsWebPanels: boolean;
}

export type DesignType =
  | typeof DESIGN_DATA_TYPE_PANELS
  | typeof DESIGN_DATA_TYPE_STENCILS
  | typeof DESIGN_DATA_TYPE_IMAGES
  | typeof DESIGN_DATA_TYPE_DESIGN_SYSTEM
  | typeof DESIGN_DATA_TYPE_FONTS;

export type DesignObjectType = {
  [Key in DesignType]: string;
};

export type ItemLoadedInfo = {
  designType: DesignType;
  designDataDetail: DesignItemDataDetail;
};

export type TextEditorType = "code" | "html" | "styles" | "tokens";

export type ReactiveSettings = {
  path: boolean;
  style: boolean;
  module: boolean;
  importAsWebPanels: boolean;
};

export type ObjectTreeCheckedStatus = {
  all: [];
  none: ["-"];
};

type ReactiveSettingsValues = {
  path: string;
  style: string;
  moduleId: string;
  importAsWebPanels: boolean;
};
